// src/pages/Competitions.jsx

import { useState, useEffect, useMemo } from "react";
import { getCompetitionTypeDisplay, getCompetitionRegistration } from '../lib/competitionUtils';
import CompetitionCard from '../components/CompetitionCard';
import { supabase } from "../lib/supabaseClient";
import analytics from '../lib/analytics';
import notify from '../lib/notify';
import { isAtLeastAge } from "../lib/ageUtils";
import SidebarNavbar from "../components/SidebarNavbar";
import BottomNavbar from "../components/BottomNavbar";
import { useNavigate } from "react-router-dom";
import Profile from "../components/ProfileForm";
import { Link } from "react-router-dom";
import { Button as FcButton, Card as FcCard, Input as FcInput, Select as FcSelect } from '../ui';
import { Dialog as FcDialog } from '../ui';

export default function Competitions({ session, userRole, username }) {
  const isAlreadyRegisteredError = (error) => {
    if (!error) return false;
    return error.code === "23505" || (error.message || "").toLowerCase().includes("duplicate key");
  };

  const navigate = useNavigate();
  // Logout handler
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };
  const [competitions, setCompetitions] = useState([]);
  const [filteredCompetitions, setFilteredCompetitions] = useState([]);
  const [userSelections, setUserSelections] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [filterLeague, setFilterLeague] = useState("");
  const [filterSeason, setFilterSeason] = useState("");
  const [filterStatus, setFilterStatus] = useState("open");
  const [filterVisibility, setFilterVisibility] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const competitionsPerPage = 9;
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [recentlyJoined, setRecentlyJoined] = useState(null);
  const [userId, setUserId] = useState(null);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [keyError, setKeyError] = useState("");
  const [pendingComp, setPendingComp] = useState(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [now, setNow] = useState(Date.now);
  useEffect(()=>{
    const timer=setInterval(()=>setNow(Date.now()),1000);
    return ()=>clearInterval(timer);
  },[]);

  const getCompetitionRoute = (competitionId, competitionType) =>
    competitionType === 'world_stage'
      ? `/world-stage/${competitionId}`
      : `/competition/${competitionId}`;

  useEffect(() => {
    const fetchUserAndCompetitions = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setUserId(user?.id);

      const [{ data: competitionsData, error: compError }, { data: selectionsData }] =
        await Promise.all([
          supabase
            .from("competitions")
            .select("*")
            .neq("type", "scout") // Exclude Scout competitions from Competitions page
            .neq("type", "pontoon") // Pontoon side-games aren't publicly joinable (auto-enrol only)
            .order("start_date", { ascending: true }),
          supabase.from("user_selections").select("*").eq("user_id", user?.id),
        ]);

      if (compError) {
        return;
      }

      // Batch-fetch matchdays and league logos for all competitions to avoid N requests.
      let competitionsWithKickoff = [];
      try {
        // Build set of (league, season) pairs present in competitions
        const comps = competitionsData || [];
        const leagueSeasonPairs = comps
          .filter(c => c.league && c.season && c.start_match_day)
          .map(c => `${c.league}|||${c.season}`);
        const uniquePairs = [...new Set(leagueSeasonPairs)];

        let matchdays = [];
        if (uniquePairs.length) {
          // Extract unique leagues and seasons to narrow the query
          const leagues = [...new Set(comps.map(c => c.league).filter(Boolean))];
          const seasons = [...new Set(comps.map(c => c.season).filter(Boolean))];

          const { data: matchdayRows, error: mdError } = await supabase
            .from("matchdays_view")
            .select("league,season,match_day,first_kickoff_time")
            .in("league", leagues)
            .in("season", seasons);

          if (mdError) {
          } else {
            matchdays = matchdayRows || [];
          }
        }

        // Build lookup map: league|||season|||match_day -> first_kickoff_time
        const mdMap = {};
        for (const md of matchdays) {
          const key = `${md.league}|||${md.season}|||${md.match_day}`;
          mdMap[key] = md.first_kickoff_time;
        }

        // Fetch league logos and flags from api_football_leagues
        const leagueNames = [...new Set(comps.map(c => c.league).filter(Boolean))];
        const leagueLogoMap = {};
        if (leagueNames.length > 0) {
          const { data: leaguesData } = await supabase
            .from("api_football_leagues")
            .select("league_name, logo, flag")
            .in("league_name", leagueNames);
          
          if (leaguesData) {
            for (const league of leaguesData) {
              leagueLogoMap[league.league_name] = {
                logo: league.logo,
                flag: league.flag
              };
            }
          }
        }

        competitionsWithKickoff = (competitionsData || []).map(comp => {
          const key = `${comp.league}|||${comp.season}|||${comp.start_match_day}`;
          const leagueData = leagueLogoMap[comp.league] || {};
          return {
            ...comp,
            first_kickoff_time: mdMap[key] ?? null,
            league_logo_url: leagueData.logo,
            league_flag_url: leagueData.flag
          };
        });
      } catch (err) {
        competitionsWithKickoff = (competitionsData || []).map(comp => ({ ...comp, first_kickoff_time: null }));
      }

      setCompetitions(competitionsWithKickoff);
      setUserSelections(selectionsData || []);
    };

    fetchUserAndCompetitions();
  }, []);

  useEffect(() => {
    let result = competitions;
    if (filterType) result = result.filter((c) => c.type === filterType);
    if (filterLeague) result = result.filter((c) => c.league === filterLeague);
    if (filterSeason) result = result.filter((c) => c.season === filterSeason);
    if (filterStatus) result = result.filter((c) => getCompetitionRegistration(c, now).state === filterStatus);
    if (filterVisibility) result = result.filter((c) => c.visibility === filterVisibility);

    setFilteredCompetitions(result);
  }, [filterType, filterLeague, filterSeason, filterStatus, filterVisibility, competitions, now]);

  useEffect(()=>{
    setCurrentPage(1);
  }, [filterType, filterLeague, filterSeason, filterStatus, filterVisibility, competitions]);

  useEffect(()=>{
    setCurrentPage(page=>Math.min(page,Math.max(1,Math.ceil(filteredCompetitions.length / competitionsPerPage))));
  },[filteredCompetitions.length]);

  const handleJoin = async (compId, compLeague, compSeason, compObj) => {
    if (!getCompetitionRegistration(compObj).canRegister) {
      notify('Registration is closed or unavailable.', 'info');
      return;
    }
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      notify("Please log in first!", 'info');
      return;
    }
    const { data: profile } = await supabase
      .from("profiles")
      .select("username, date_of_birth")
      .eq("id", user.id)
      .single();
    if (!profile || !profile.username) {
      setShowProfileModal(true);
      return;
    }
    if (compObj?.is_18_plus && !isAtLeastAge(profile?.date_of_birth, 18)) {
      notify("This competition is 18+ only.", 'error');
      return;
    }
    const { data: existing, error: fetchError } = await supabase
      .from("user_selections")
      .select("*")
      .eq("user_id", user.id)
      .eq("competition_id", compId);
    if (fetchError) {
      notify("Error checking competition join status.", 'error');
      return;
    }
    if (existing.length > 0) {
      notify("You have already joined this competition!", 'info');
      return;
    }
    // If private, show key modal
    if (compObj.visibility === "private") {
      setPendingComp({ compId, compLeague, compSeason, compObj });
      setShowKeyModal(true);
      analytics.event('private_join_attempt', { competition_id: compId, competition_name: compObj.name });
      return;
    }
    // Public competition: perform registration
    if (!getCompetitionRegistration(compObj).canRegister) {
      notify('Registration is closed or unavailable.', 'info');
      return;
    }
    const { error: joinError } = await supabase
      .from("user_selections")
      .insert([{ user_id: user.id, competition_id: compId, league: compLeague, season: compSeason, match_day: compObj.start_match_day, status: compObj.requires_approval ? 'pending' : 'active' }]);
    if (joinError) {
      if (isAlreadyRegisteredError(joinError)) {
        notify("You have already joined this competition!", 'info');
        return;
      }
      notify("Failed to join competition: " + joinError.message, 'error');
      return;
    }
    setUserSelections((prev) => [...prev, { user_id: user.id, competition_id: compId, league: compLeague, season: compSeason, match_day: compObj.start_match_day }]);
    analytics.event('competition_register', { competition_id: compId, competition_name: compObj.name, visibility: compObj.visibility });
  notify("Successfully joined competition!", 'success');
  };
  // Registration logic for private competitions (modal submit)
  const handlePrivateJoin = async (e) => {
    e.preventDefault();
    if (!pendingComp) return;
    const {
      compId,
      compObj
    } = pendingComp;
    if (!getCompetitionRegistration(compObj).canRegister) {
      setKeyError('Registration is closed or unavailable.');
      return;
    }
    // Validate the registration key against the stored competition key
    try {
      const { data: compData, error: compErr } = await supabase
        .from('competitions')
        .select('registration_key')
        .eq('id', compId)
        .single();
        if (compErr) {
          setKeyError('Failed to validate registration key. Please try again.');
          return;
        }
      const storedKey = compData?.registration_key || '';
      if (String(storedKey).trim() !== String(keyInput || '').trim()) {
        setKeyError('Invalid registration key. Please check and try again.');
        return;
      }
    } catch (err) {
      setKeyError('Failed to validate registration key. Please try again.');
      return;
    }
    const { data: { user } = {} } = await supabase.auth.getUser();
    if (!user) {
      setKeyError("You must be logged in.");
      return;
    }

    const { data: profile } = await supabase
      .from("profiles")
      .select("date_of_birth")
      .eq("id", user.id)
      .maybeSingle();

    if (compObj?.is_18_plus && !isAtLeastAge(profile?.date_of_birth, 18)) {
      setKeyError("This competition is 18+ only.");
      return;
    }

    // Insert into user_selections
    if (!getCompetitionRegistration(compObj).canRegister) {
      setKeyError('Registration is closed or unavailable.');
      return;
    }
    const { error: joinError } = await supabase
      .from("user_selections")
      .insert([{ user_id: user.id, competition_id: compId, league: compObj.league, season: compObj.season, match_day: compObj.start_match_day, status: compObj.requires_approval ? 'pending' : 'active' }]);
    if (joinError) {
      if (isAlreadyRegisteredError(joinError)) {
        setShowKeyModal(false);
        setKeyInput("");
        setKeyError("");
        setPendingComp(null);
        notify("You are already registered for this competition.", 'info');
        return;
      }
      setKeyError("Failed to join competition: " + joinError.message);
      return;
    }
    setUserSelections((prev) => [...prev, { user_id: user.id, competition_id: compId, league: compObj.league, season: compObj.season, match_day: compObj.start_match_day }]);
    setShowKeyModal(false);
    setKeyInput("");
    setKeyError("");
    setPendingComp(null);
  analytics.event('private_join_success', { competition_id: compId, competition_name: compObj.name });
  notify("Successfully joined competition!", 'success');
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredCompetitions.length / competitionsPerPage);
  const paginatedCompetitions = filteredCompetitions.slice(
    (currentPage - 1) * competitionsPerPage,
    currentPage * competitionsPerPage
  );

  const leagueOptions = useMemo(() => {
    return [...new Set((competitions || []).map((comp) => comp.league).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
  }, [competitions]);

  const seasonOptions = useMemo(() => {
    const seasonSource = filterLeague
      ? (competitions || []).filter((comp) => comp.league === filterLeague)
      : (competitions || []);
    return [...new Set(seasonSource.map((comp) => comp.season).filter(Boolean))]
      .sort((a, b) => b.localeCompare(a));
  }, [competitions, filterLeague]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (filterType) {
      chips.push({ key: "type", label: `Type: ${getCompetitionTypeDisplay(filterType)}`, onClear: () => setFilterType("") });
    }
    if (filterLeague) {
      chips.push({ key: "league", label: `League: ${filterLeague}`, onClear: () => setFilterLeague("") });
    }
    if (filterSeason) {
      chips.push({ key: "season", label: `Season: ${filterSeason}`, onClear: () => setFilterSeason("") });
    }
    // `open` is the default baseline state for this page, so only show
    // a chip when the status differs from that baseline.
    if (filterStatus && filterStatus !== "open") {
      const statusLabel = filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1);
      chips.push({ key: "status", label: `Registration: ${statusLabel}`, onClear: () => setFilterStatus("open") });
    }
    if (filterVisibility) {
      const visibilityLabel = filterVisibility.charAt(0).toUpperCase() + filterVisibility.slice(1);
      chips.push({ key: "visibility", label: `Visibility: ${visibilityLabel}`, onClear: () => setFilterVisibility("") });
    }
    return chips;
  }, [filterType, filterLeague, filterSeason, filterStatus, filterVisibility]);

  // Reset filters helper
  const resetFilters = () => {
    setFilterType("");
    setFilterLeague("");
    setFilterSeason("");
    setFilterStatus("open");
    setFilterVisibility("");
  };

  return (
    <div className="min-h-screen bg-surface-canvas">
      {/* Fixed green banner at the top, only on mobile/tablet */}
      <div className="fixed top-0 left-0 w-full z-40 bg-ui-green text-white py-3 font-bold text-lg shadow-md flex items-center md:hidden">
        <span className="ml-6">Footy Coach</span>
      </div>
      <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
      <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
      <div role="main" id="fc-main-content" className="md:ml-64 pt-20 md:pt-8 px-2 sm:px-4 md:p-8 pb-24 md:pb-8 fc-workspace">
        <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-brand-text">
          Competitions ({filteredCompetitions.length})
        </h1>
        {/* Filters */}
        <FcCard as="div" padding="standard" className="bg-surface-card border border-ui-border rounded-xl shadow-sm p-4 sm:p-5 mb-6">
          <div className="md:hidden flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-wide text-content-secondary">Filter options</span>
            <FcButton variant="tertiary"
              type="button"
              onClick={() => setShowMobileFilters((prev) => !prev)}
              className="hover:underline"
            >
              {showMobileFilters ? 'Hide Filters' : 'Show Filters'}
            </FcButton>
          </div>

          <div className={`${showMobileFilters ? 'grid' : 'hidden'} md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3`}>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold uppercase tracking-wide text-content-secondary">Type</label>
              <FcSelect aria-label="Type"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="border"
              >
                <option value="">All Types</option>
                <option value="knockout">Knockout</option>
                <option value="season">Season</option>
                <option value="world_stage">World Stage</option>
              </FcSelect>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold uppercase tracking-wide text-content-secondary">League</label>
              <FcSelect aria-label="League"
                value={filterLeague}
                onChange={(e) => setFilterLeague(e.target.value)}
                className="border"
              >
                <option value="">All Leagues</option>
                {leagueOptions.map((league) => (
                  <option key={league} value={league}>{league}</option>
                ))}
              </FcSelect>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold uppercase tracking-wide text-content-secondary">Season</label>
              <FcSelect aria-label="Season"
                value={filterSeason}
                onChange={(e) => setFilterSeason(e.target.value)}
                className="border"
              >
                <option value="">All Seasons</option>
                {seasonOptions.map((season) => (
                  <option key={season} value={season}>{season}</option>
                ))}
              </FcSelect>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold uppercase tracking-wide text-content-secondary">Registration</label>
              <FcSelect
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border"
                aria-label="Registration"
                title="Registration Status"
              >
                <option value="">All Registration Statuses</option>
                <option value="open">Open</option>
                <option value="closed">Closed</option>
              </FcSelect>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-bold uppercase tracking-wide text-content-secondary">Visibility</label>
              <FcSelect aria-label="Visibility"
                value={filterVisibility}
                onChange={(e) => setFilterVisibility(e.target.value)}
                className="border"
              >
                <option value="">All Visibility</option>
                <option value="public">Public</option>
                <option value="private">Private</option>
              </FcSelect>
            </div>
          </div>

          <div className={`${showMobileFilters ? 'flex' : 'hidden'} md:flex mt-3 justify-end`}>
            <FcButton variant="primary" type="submit"
              onClick={resetFilters}
              className="disabled:cursor-not-allowed"
              disabled={activeFilterChips.length === 0}
            >
              Reset Filters
            </FcButton>
          </div>
        </FcCard>

        {activeFilterChips.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-5 -mt-2">
            {activeFilterChips.map((chip) => (
              <FcButton variant="secondary" type="submit"
                key={chip.key}
                onClick={chip.onClear}
                className="inline-flex items-center gap-2 border"
              >
                <span>{chip.label}</span>
                <span aria-hidden="true">×</span>
              </FcButton>
            ))}
            <FcButton variant="tertiary" type="submit"
              onClick={resetFilters}
              className="hover:underline"
            >
              Clear all
            </FcButton>
          </div>
        )}
        {/* Competition Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-6">
          {paginatedCompetitions.map((comp) => {
            const joined = userSelections.some((sel) => sel.competition_id === comp.id);
            const canRegister = getCompetitionRegistration(comp, now).canRegister;
            return (
              <CompetitionCard
                key={comp.id}
                comp={comp}
                joined={joined}
                canRegister={canRegister}
                onJoin={(c) => handleJoin(c.id, c.league, c.season, c)}
                getRoute={getCompetitionRoute}
                leagueBranding inlineActions now={now}
              />
            );
          })}
          {paginatedCompetitions.length === 0 && (
            <FcCard as="div" padding="standard" className="col-span-full text-center border border-ui-border bg-surface-card rounded-lg p-6">
              <p className="text-brand-text-muted mb-3">No competitions match your filters.</p>
              <FcButton variant="primary" type="submit"
                onClick={resetFilters}
                className=""
              >
                Clear Filters
              </FcButton>
            </FcCard>
          )}
        </div>
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex flex-wrap sm:flex-nowrap justify-center items-center mt-6 mb-24 md:mb-2 gap-2 sm:gap-4">
            <FcButton variant="secondary" type="submit"
              className={`border ${currentPage === 1 ? "cursor-not-allowed" : ""}`}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Prev
            </FcButton>
            <span className="font-semibold text-brand-text text-sm sm:text-base px-2">
              <span className="sm:hidden">{currentPage}/{totalPages}</span>
              <span className="hidden sm:inline">Page {currentPage} of {totalPages}</span>
            </span>
            <FcButton variant="secondary" type="submit"
              className={`border ${currentPage === totalPages ? "cursor-not-allowed" : ""}`}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </FcButton>
          </div>
        )}
        {/* Registration Key Modal for Private Competitions */}
        {showKeyModal && pendingComp && (
          <FcDialog open title="Enter Registration Key" size="standard" onCancel={() => {
                       setShowKeyModal(false);
                       setKeyInput("");
                       setKeyError("");
                       setPendingComp(null);
                     }}>
            <div className="bg-brand-white rounded-lg shadow-lg p-6 w-80">

              <form onSubmit={handlePrivateJoin} className="space-y-4">
                 <FcInput aria-label="Registration Key"
                   type="text"
                   placeholder="Registration Key"
                   value={keyInput}
                   onChange={e => setKeyInput(e.target.value)}
                   className="block w-full border"
                   required
                 />
                 {keyError && <div className="text-ui-error text-sm">{keyError}</div>}
                 <div className="flex gap-2 justify-end">
                   <FcButton variant="tertiary"
                     type="button"
                     className=""
                     onClick={() => {
                       setShowKeyModal(false);
                       setKeyInput("");
                       setKeyError("");
                       setPendingComp(null);
                     }}
                   >
                     Cancel
                   </FcButton>
                   <FcButton variant="primary"
                     type="submit"
                     className=""
                   >
                     Join
                   </FcButton>
                 </div>
               </form>
            </div>
          </FcDialog>
        )}
      </div>
    </div>
  );
}
